import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import ChatBot from '../components/ChatBot';
import { useToast } from '../components/ui/Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Section configuration
const SECTIONS = [
  {
    id: 'contact',
    title: 'Contact Information',
    icon: '👤',
    description: 'Name, email, phone, LinkedIn, GitHub, portfolio',
    color: 'var(--indigo-500)'
  },
  {
    id: 'education',
    title: 'Education',
    icon: '🎓',
    description: 'Schools, degrees, majors, GPA, coursework',
    color: 'var(--emerald-500)'
  },
  {
    id: 'experience',
    title: 'Professional Experience',
    icon: '💼',
    description: 'Jobs, internships, work history',
    color: 'var(--blue-500)'
  },
  {
    id: 'projects',
    title: 'Projects',
    icon: '🚀',
    description: 'Personal, hackathon, and university projects',
    color: 'var(--purple-500)'
  },
  {
    id: 'skills',
    title: 'Technical Skills',
    icon: '⚡',
    description: 'Languages, frameworks, tools, databases',
    color: 'var(--amber-500)'
  },
  {
    id: 'certifications',
    title: 'Certifications',
    icon: '📜',
    description: 'Professional certifications and credentials',
    color: 'var(--cyan-500)'
  },
  {
    id: 'honors',
    title: 'Honors & Awards',
    icon: '🏆',
    description: 'Academic and professional achievements',
    color: 'var(--rose-500)'
  },
  {
    id: 'leadership',
    title: 'Leadership & Extracurriculars',
    icon: '🌟',
    description: 'Organizations, clubs, volunteer work',
    color: 'var(--teal-500)'
  },
  {
    id: 'job_preferences',
    title: 'Job Preferences',
    icon: '🎯',
    description: 'Target roles, locations, remote preferences',
    color: 'var(--orange-500)'
  }
];

const Profile = () => {
  const { getToken, refreshUserProfile, userProfile, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const [resumeData, setResumeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const [selectedBullet, setSelectedBullet] = useState(null);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [userPreferences, setUserPreferences] = useState(null);

  // Load master resume data
  const loadMasterResume = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();

      // First check if master resume exists
      const infoResponse = await fetch(`${API_URL}/resume/master-resume-info`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const info = await infoResponse.json();

      let prepared;
      if (info.has_master) {
        // Download the master resume data
        const response = await fetch(`${API_URL}/resume/download-master`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await response.json();
        prepared = prepareResumeData(data);
      } else {
        // Initialize empty resume
        prepared = getEmptyResumeData();
      }

      // Pre-populate name from userProfile if not already set (from onboarding)
      if (!prepared.contact_info.full_name && userProfile?.full_name) {
        prepared.contact_info.full_name = userProfile.full_name;
      }
      // Also fallback to email if not already set
      if (!prepared.contact_info.email && user?.email) {
        prepared.contact_info.email = user.email;
      }

      setResumeData(prepared);
    } catch (err) {
      console.error('Failed to load master resume:', err);
      const empty = getEmptyResumeData();
      // Pre-populate even on error
      if (userProfile?.full_name) {
        empty.contact_info.full_name = userProfile.full_name;
      }
      if (user?.email) {
        empty.contact_info.email = user.email;
      }
      setResumeData(empty);
    } finally {
      setLoading(false);
    }
  }, [getToken, userProfile, user]);

  // Load user preferences from onboarding status
  const loadUserPreferences = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/api/onboarding/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUserPreferences({
        career_status: data.career_status || '',
        target_roles: data.target_roles || [],
        preferred_locations: data.preferred_locations || [],
        remote_preference: data.remote_preference || '',
        years_experience: data.years_experience ?? ''
      });
    } catch (err) {
      console.error('Failed to load user preferences:', err);
    }
  }, [getToken]);

  useEffect(() => {
    loadMasterResume();
    loadUserPreferences();
  }, [loadMasterResume, loadUserPreferences]);

  // Prepare resume data (convert arrays to comma-separated strings for editing)
  const prepareResumeData = (data) => {
    if (!data) return getEmptyResumeData();

    // Helper to join array or return string
    const joinArray = (arr) => Array.isArray(arr) ? arr.join(', ') : (arr || '');

    return {
      contact_info: {
        full_name: data.contact_info?.full_name || '',
        email: data.contact_info?.email || '',
        phone: data.contact_info?.phone || '',
        location: data.contact_info?.location || '',
        linkedin_url: data.contact_info?.linkedin_url || '',
        github_url: data.contact_info?.github_url || '',
        portfolio_url: data.contact_info?.portfolio_url || '',
        other_links: data.contact_info?.other_links || [],
        visibility: data.contact_info?.visibility || {
          email: true,
          phone: true,
          linkedin: true,
          github: true,
          portfolio: true
        }
      },
      education: data.education?.map(edu => ({
        school_name: edu.school_name || '',
        degree: edu.degree || '',
        major: edu.major || '',
        minor: edu.minor || '',
        graduation_date: edu.graduation_date || '',
        gpa: edu.gpa ?? '',
        coursework: joinArray(edu.coursework),
        honors: joinArray(edu.honors)
      })) || [],
      experience: data.experience?.map(exp => ({
        company_name: exp.company_name || '',
        job_title: exp.job_title || '',
        location: exp.location || '',
        start_date: exp.start_date || '',
        end_date: exp.end_date || '',
        bullets: exp.bullets || [],
        technologies: joinArray(exp.technologies),
        is_favorite: exp.is_favorite || false
      })) || [],
      projects: data.projects?.map(proj => ({
        project_name: proj.project_name || '',
        context: proj.context || 'Personal',
        date: proj.date || '',
        bullets: proj.bullets || [],
        technologies: joinArray(proj.technologies),
        github_link: proj.github_link || '',
        demo_link: proj.demo_link || '',
        is_favorite: proj.is_favorite || false
      })) || [],
      skills: {
        languages: joinArray(data.skills?.languages),
        frontend: joinArray(data.skills?.frontend),
        backend: joinArray(data.skills?.backend),
        databases: joinArray(data.skills?.databases),
        cloud_devops: joinArray(data.skills?.cloud_devops),
        tools: joinArray(data.skills?.tools)
      },
      certifications: data.certifications?.map(cert => ({
        cert_name: cert.cert_name || '',
        issuing_org: cert.issuing_org || '',
        date_obtained: cert.date_obtained || '',
        expiration_date: cert.expiration_date || ''
      })) || [],
      honors: data.honors?.map(honor => ({
        award_name: honor.award_name || '',
        issuing_org: honor.issuing_org || '',
        date: honor.date || '',
        description: honor.description || ''
      })) || [],
      leadership: data.leadership?.map(lead => ({
        organization: lead.organization || '',
        role: lead.role || '',
        dates: lead.dates || '',
        achievements: joinArray(lead.achievements)
      })) || []
    };
  };

  const getEmptyResumeData = () => ({
    contact_info: {
      full_name: '',
      email: '',
      phone: '',
      location: '',
      linkedin_url: '',
      github_url: '',
      portfolio_url: '',
      other_links: [],
      visibility: {
        email: true,
        phone: true,
        linkedin: true,
        github: true,
        portfolio: true
      }
    },
    education: [],
    experience: [],
    projects: [],
    skills: {
      languages: '',
      frontend: '',
      backend: '',
      databases: '',
      cloud_devops: '',
      tools: ''
    },
    certifications: [],
    honors: [],
    leadership: []
  });

  // Save master resume
  const saveMasterResume = async () => {
    try {
      setSaving(true);
      const token = getToken();

      // Helper to convert comma-separated string to array
      const toArray = (val) => {
        if (Array.isArray(val)) return val;
        if (typeof val === 'string' && val.trim()) {
          return val.split(',').map(s => s.trim()).filter(Boolean);
        }
        return [];
      };

      // Helper to parse GPA (convert empty string to null)
      const parseGpa = (val) => {
        if (val === '' || val === null || val === undefined) return null;
        const num = parseFloat(val);
        return isNaN(num) ? null : num;
      };

      // Convert string fields back to arrays for API
      const apiData = {
        contact_info: {
          full_name: resumeData.contact_info?.full_name || '',
          email: resumeData.contact_info?.email || '',
          phone: resumeData.contact_info?.phone || '',
          location: resumeData.contact_info?.location || null,
          linkedin_url: resumeData.contact_info?.linkedin_url || null,
          github_url: resumeData.contact_info?.github_url || null,
          portfolio_url: resumeData.contact_info?.portfolio_url || null,
          other_links: resumeData.contact_info?.other_links || [],
          visibility: resumeData.contact_info?.visibility || {
            email: true,
            phone: true,
            linkedin: true,
            github: true,
            portfolio: true
          }
        },
        education: resumeData.education?.map(edu => ({
          school_name: edu.school_name || '',
          degree: edu.degree || '',
          major: edu.major || '',
          minor: edu.minor || null,
          graduation_date: edu.graduation_date || '',
          gpa: parseGpa(edu.gpa),
          coursework: toArray(edu.coursework),
          honors: toArray(edu.honors)
        })) || [],
        experience: resumeData.experience?.map(exp => ({
          company_name: exp.company_name || '',
          job_title: exp.job_title || '',
          location: exp.location || '',
          start_date: exp.start_date || '',
          end_date: exp.end_date || '',
          bullets: exp.bullets || [],
          technologies: toArray(exp.technologies),
          is_favorite: exp.is_favorite || false
        })) || [],
        projects: resumeData.projects?.map(proj => ({
          project_name: proj.project_name || '',
          context: proj.context || 'Personal',
          date: proj.date || '',
          bullets: proj.bullets || [],
          technologies: toArray(proj.technologies),
          github_link: proj.github_link || null,
          demo_link: proj.demo_link || null,
          is_favorite: proj.is_favorite || false
        })) || [],
        skills: {
          languages: toArray(resumeData.skills?.languages),
          frontend: toArray(resumeData.skills?.frontend),
          backend: toArray(resumeData.skills?.backend),
          databases: toArray(resumeData.skills?.databases),
          cloud_devops: toArray(resumeData.skills?.cloud_devops),
          tools: toArray(resumeData.skills?.tools)
        },
        certifications: resumeData.certifications?.map(cert => ({
          cert_name: cert.cert_name || '',
          issuing_org: cert.issuing_org || '',
          date_obtained: cert.date_obtained || '',
          expiration_date: cert.expiration_date || null
        })) || [],
        honors: resumeData.honors?.map(honor => ({
          award_name: honor.award_name || '',
          issuing_org: honor.issuing_org || '',
          date: honor.date || '',
          description: honor.description || null
        })) || [],
        leadership: resumeData.leadership?.map(lead => ({
          organization: lead.organization || '',
          role: lead.role || '',
          dates: lead.dates || '',
          achievements: toArray(lead.achievements)
        })) || []
      };

      console.log('Saving resume data:', apiData);

      const response = await fetch(`${API_URL}/resume/master-upload-manual`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Save error response:', errorData);
        throw new Error(errorData.detail || `Save failed with status ${response.status}`);
      }

      setHasUnsavedChanges(false);
      showSuccess('Changes saved successfully!');
      // Refresh user profile to update name in sidebar/dashboard
      refreshUserProfile();
    } catch (err) {
      console.error('Failed to save master resume:', err);
      showError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Save job preferences using onboarding API
  const saveJobPreferences = async (prefsData) => {
    try {
      setSaving(true);
      const token = getToken();

      const response = await fetch(`${API_URL}/api/onboarding/step/2`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          career_status: prefsData.career_status,
          target_roles: prefsData.target_roles,
          preferred_locations: prefsData.preferred_locations,
          remote_preference: prefsData.remote_preference,
          years_experience: prefsData.years_experience ? parseInt(prefsData.years_experience) : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save job preferences');
      }

      setUserPreferences(prev => ({ ...prev, ...prefsData }));
      showSuccess('Job preferences saved!');
      setActiveSection(null);
    } catch (err) {
      console.error('Failed to save job preferences:', err);
      showError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Update a specific field in resume data (local only, no auto-save)
  const updateField = (path, value) => {
    const pathParts = path.split(/\.|\[|\]/).filter(Boolean);
    const newData = JSON.parse(JSON.stringify(resumeData));

    let current = newData;
    for (let i = 0; i < pathParts.length - 1; i++) {
      current = current[pathParts[i]];
    }
    current[pathParts[pathParts.length - 1]] = value;

    setResumeData(newData);
    setHasUnsavedChanges(true);
  };

  // Handle AI bullet update
  const handleBulletUpdate = (bulletInfo, newBulletText) => {
    const { section, sectionIndex, bulletIndex } = bulletInfo;

    if (section === 'experience') {
      const newExperience = [...resumeData.experience];
      newExperience[sectionIndex].bullets[bulletIndex] = newBulletText;
      updateField('experience', newExperience);
    } else if (section === 'project') {
      const newProjects = [...resumeData.projects];
      newProjects[sectionIndex].bullets[bulletIndex] = newBulletText;
      updateField('projects', newProjects);
    }
  };

  // Get section summary for cards
  const getSectionSummary = (sectionId) => {
    if (!resumeData) return null;

    switch (sectionId) {
      case 'contact':
        return resumeData.contact_info?.full_name || 'Not set';
      case 'education':
        return `${resumeData.education?.length || 0} entries`;
      case 'experience':
        return `${resumeData.experience?.length || 0} positions`;
      case 'projects':
        return `${resumeData.projects?.length || 0} projects`;
      case 'skills':
        const skillCount = [
          resumeData.skills?.languages,
          resumeData.skills?.frontend,
          resumeData.skills?.backend,
          resumeData.skills?.databases,
          resumeData.skills?.cloud_devops,
          resumeData.skills?.tools
        ].filter(s => s && s.length > 0).length;
        return `${skillCount} categories`;
      case 'certifications':
        return `${resumeData.certifications?.length || 0} certifications`;
      case 'honors':
        return `${resumeData.honors?.length || 0} awards`;
      case 'leadership':
        return `${resumeData.leadership?.length || 0} activities`;
      case 'job_preferences':
        if (!userPreferences) return 'Not set';
        const roleCount = userPreferences.target_roles?.length || 0;
        return roleCount > 0 ? `${roleCount} target role${roleCount !== 1 ? 's' : ''}` : 'Not set';
      default:
        return null;
    }
  };

  const styles = {
    container: {
      padding: '32px',
      maxWidth: '1400px',
      margin: '0 auto'
    },
    header: {
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '700',
      marginBottom: '8px',
      color: 'var(--text-primary)'
    },
    subtitle: {
      color: 'var(--text-tertiary)',
      fontSize: '16px'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '20px'
    },
    card: (isHovered, color) => ({
      padding: '24px',
      background: 'var(--bg-card)',
      borderRadius: '16px',
      border: '1px solid var(--border-subtle)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      transform: isHovered ? 'translateY(-4px)' : 'none',
      boxShadow: isHovered ? `0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px ${color}40` : 'none'
    }),
    cardIcon: {
      fontSize: '32px',
      marginBottom: '12px'
    },
    cardTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '4px',
      color: 'var(--text-primary)'
    },
    cardDescription: {
      fontSize: '14px',
      color: 'var(--text-tertiary)',
      marginBottom: '12px'
    },
    cardSummary: (color) => ({
      fontSize: '13px',
      fontWeight: '500',
      color: color,
      display: 'flex',
      alignItems: 'center',
      gap: '6px'
    }),
    // Editor panel styles
    editorOverlay: {
      position: 'fixed',
      top: 0,
      left: 'var(--sidebar-width)',
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      backdropFilter: 'blur(4px)',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      transition: 'left 0.3s ease'
    },
    editorPanel: {
      width: '90%',
      maxWidth: '900px',
      maxHeight: '90vh',
      background: 'var(--bg-card)',
      borderRadius: '20px',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column'
    },
    editorHeader: {
      padding: '20px 24px',
      borderBottom: '1px solid var(--border-subtle)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    editorTitle: {
      fontSize: '20px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    editorContent: {
      flex: 1,
      overflowY: 'auto',
      padding: '24px'
    },
    closeButton: {
      background: 'var(--bg-surface)',
      border: 'none',
      borderRadius: '8px',
      padding: '8px 16px',
      color: 'var(--text-primary)',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Profile" subtitle="Your Master Resume">
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '400px',
          color: 'var(--text-tertiary)'
        }}>
          Loading your profile...
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Profile" subtitle="Your Master Resume">
      <div style={styles.container}>
        <div style={styles.header}>
          <h1 style={styles.title}>Master Resume</h1>
          <p style={styles.subtitle}>
            Select a section below to edit. Click Save when you're done.
          </p>
        </div>

        <div style={styles.grid}>
          {SECTIONS.map(section => (
            <SectionCard
              key={section.id}
              section={section}
              summary={getSectionSummary(section.id)}
              onClick={() => setActiveSection(section.id)}
              styles={styles}
            />
          ))}
        </div>

        {/* Section Editor Modal */}
        {activeSection && (
          <div style={styles.editorOverlay} onClick={() => setActiveSection(null)}>
            <div style={styles.editorPanel} onClick={e => e.stopPropagation()}>
              <div style={styles.editorHeader}>
                <div style={styles.editorTitle}>
                  <span>{SECTIONS.find(s => s.id === activeSection)?.icon}</span>
                  <span>{SECTIONS.find(s => s.id === activeSection)?.title}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Hide AI Assistant and Save buttons for preference sections (they have their own save buttons) */}
                  {activeSection !== 'job_preferences' && (
                    <>
                      <button
                        style={{
                          background: 'rgba(99, 102, 241, 0.2)',
                          border: '1px solid rgba(99, 102, 241, 0.4)',
                          borderRadius: '8px',
                          padding: '8px 16px',
                          color: 'var(--text-primary)',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                        onClick={() => setShowChatbot(!showChatbot)}
                      >
                        {showChatbot ? '✕ Close' : '✨'} AI Assistant
                      </button>
                      <button
                        style={{
                          background: hasUnsavedChanges ? 'var(--gradient-primary)' : 'var(--bg-surface)',
                          border: 'none',
                          borderRadius: '8px',
                          padding: '10px 20px',
                          color: hasUnsavedChanges ? 'white' : 'var(--text-tertiary)',
                          cursor: saving ? 'wait' : 'pointer',
                          fontSize: '14px',
                          fontWeight: '600',
                          opacity: saving ? 0.7 : 1,
                          transition: 'all 0.2s ease'
                        }}
                        onClick={saveMasterResume}
                        disabled={saving}
                        onMouseOver={(e) => {
                          if (hasUnsavedChanges && !saving) {
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(99, 102, 241, 0.3)';
                          }
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        {saving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  )}
                  <button style={styles.closeButton} onClick={() => setActiveSection(null)}>
                    Close
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                <div style={{ ...styles.editorContent, flex: showChatbot ? '1' : '1' }}>
                  <SectionEditor
                    section={activeSection}
                    data={resumeData}
                    onUpdate={updateField}
                    onSelectBullet={setSelectedBullet}
                    selectedBullet={selectedBullet}
                    userPreferences={userPreferences}
                    onSaveJobPreferences={saveJobPreferences}
                    saving={saving}
                  />
                </div>

                {showChatbot && (
                  <div style={{
                    width: '400px',
                    borderLeft: '1px solid var(--border-subtle)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}>
                    <ChatBot
                      resumeData={resumeData}
                      selectedBullet={selectedBullet}
                      onBulletUpdate={handleBulletUpdate}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

// Section Card Component
const SectionCard = ({ section, summary, onClick, styles }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      style={styles.card(isHovered, section.color)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <div style={styles.cardIcon}>{section.icon}</div>
      <h3 style={styles.cardTitle}>{section.title}</h3>
      <p style={styles.cardDescription}>{section.description}</p>
      {summary && (
        <div style={styles.cardSummary(section.color)}>
          <span style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            background: section.color
          }} />
          {summary}
        </div>
      )}
    </div>
  );
};

// Section Editor Component
const SectionEditor = ({ section, data, onUpdate, onSelectBullet, selectedBullet, userPreferences, onSaveJobPreferences, saving }) => {
  switch (section) {
    case 'contact':
      return <ContactEditor data={data} onUpdate={onUpdate} />;
    case 'education':
      return <EducationEditor data={data} onUpdate={onUpdate} />;
    case 'experience':
      return <ExperienceEditor data={data} onUpdate={onUpdate} onSelectBullet={onSelectBullet} selectedBullet={selectedBullet} />;
    case 'projects':
      return <ProjectsEditor data={data} onUpdate={onUpdate} onSelectBullet={onSelectBullet} selectedBullet={selectedBullet} />;
    case 'skills':
      return <SkillsEditor data={data} onUpdate={onUpdate} />;
    case 'certifications':
      return <CertificationsEditor data={data} onUpdate={onUpdate} />;
    case 'honors':
      return <HonorsEditor data={data} onUpdate={onUpdate} />;
    case 'leadership':
      return <LeadershipEditor data={data} onUpdate={onUpdate} />;
    case 'job_preferences':
      return <JobPreferencesEditor data={userPreferences} onSave={onSaveJobPreferences} saving={saving} />;
    default:
      return null;
  }
};

// Shared styles for editors
const editorStyles = {
  fieldGroup: {
    marginBottom: '20px'
  },
  label: {
    display: 'block',
    fontSize: '13px',
    fontWeight: '500',
    color: 'var(--text-tertiary)',
    marginBottom: '8px'
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '10px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    background: 'var(--bg-surface)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '10px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '80px',
    fontFamily: 'inherit',
    boxSizing: 'border-box'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px'
  },
  card: {
    padding: '20px',
    background: 'var(--bg-surface)',
    borderRadius: '12px',
    marginBottom: '16px',
    border: '1px solid var(--border-subtle)'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px'
  },
  cardTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-secondary)'
  },
  deleteButton: {
    padding: '6px 12px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '6px',
    color: 'var(--red-500)',
    cursor: 'pointer',
    fontSize: '12px'
  },
  addButton: {
    width: '100%',
    padding: '14px',
    background: 'rgba(99, 102, 241, 0.1)',
    border: '2px dashed rgba(99, 102, 241, 0.3)',
    borderRadius: '12px',
    color: 'var(--indigo-400)',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  },
  bulletItem: (isSelected) => ({
    display: 'flex',
    gap: '8px',
    marginBottom: '8px',
    alignItems: 'flex-start',
    padding: '8px',
    borderRadius: '8px',
    background: isSelected ? 'rgba(99, 102, 241, 0.1)' : 'transparent',
    border: isSelected ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid transparent'
  }),
  bulletTextarea: {
    flex: 1,
    padding: '10px 12px',
    background: 'var(--bg-deep)',
    border: '1px solid var(--border-subtle)',
    borderRadius: '8px',
    color: 'var(--text-primary)',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    minHeight: '60px',
    fontFamily: 'inherit'
  },
  bulletDeleteBtn: {
    padding: '8px',
    background: 'rgba(239, 68, 68, 0.1)',
    border: 'none',
    borderRadius: '6px',
    color: 'var(--red-400)',
    cursor: 'pointer',
    fontSize: '14px'
  },
  addBulletBtn: {
    padding: '8px 12px',
    background: 'rgba(99, 102, 241, 0.1)',
    border: '1px dashed rgba(99, 102, 241, 0.3)',
    borderRadius: '6px',
    color: 'var(--indigo-400)',
    cursor: 'pointer',
    fontSize: '13px',
    marginTop: '8px'
  },
  visibilityToggle: (visible) => ({
    padding: '6px 12px',
    background: visible ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
    border: `1px solid ${visible ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
    borderRadius: '6px',
    color: visible ? 'var(--emerald-400)' : 'var(--red-400)',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500'
  })
};

// Contact Information Editor
const ContactEditor = ({ data, onUpdate }) => {
  const contact = data?.contact_info || {};
  const visibility = contact.visibility || {};

  const handleChange = (field, value) => {
    onUpdate(`contact_info.${field}`, value);
  };

  const handleVisibilityChange = (field) => {
    const newVisibility = { ...visibility, [field]: !visibility[field] };
    onUpdate('contact_info.visibility', newVisibility);
  };

  return (
    <div>
      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Full Name</label>
        <input
          type="text"
          style={editorStyles.input}
          value={contact.full_name || ''}
          onChange={(e) => handleChange('full_name', e.target.value)}
          placeholder="John Doe"
        />
      </div>

      <div style={editorStyles.row}>
        <div style={editorStyles.fieldGroup}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ ...editorStyles.label, marginBottom: 0 }}>Email</label>
            <button
              style={editorStyles.visibilityToggle(visibility.email !== false)}
              onClick={() => handleVisibilityChange('email')}
            >
              {visibility.email !== false ? 'Visible' : 'Hidden'}
            </button>
          </div>
          <input
            type="email"
            style={editorStyles.input}
            value={contact.email || ''}
            onChange={(e) => handleChange('email', e.target.value)}
            placeholder="john@example.com"
          />
        </div>

        <div style={editorStyles.fieldGroup}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ ...editorStyles.label, marginBottom: 0 }}>Phone</label>
            <button
              style={editorStyles.visibilityToggle(visibility.phone !== false)}
              onClick={() => handleVisibilityChange('phone')}
            >
              {visibility.phone !== false ? 'Visible' : 'Hidden'}
            </button>
          </div>
          <input
            type="tel"
            style={editorStyles.input}
            value={contact.phone || ''}
            onChange={(e) => handleChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
          />
        </div>
      </div>

      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Location</label>
        <input
          type="text"
          style={editorStyles.input}
          value={contact.location || ''}
          onChange={(e) => handleChange('location', e.target.value)}
          placeholder="San Francisco, CA"
        />
      </div>

      <div style={editorStyles.fieldGroup}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <label style={{ ...editorStyles.label, marginBottom: 0 }}>LinkedIn URL</label>
          <button
            style={editorStyles.visibilityToggle(visibility.linkedin !== false)}
            onClick={() => handleVisibilityChange('linkedin')}
          >
            {visibility.linkedin !== false ? 'Visible' : 'Hidden'}
          </button>
        </div>
        <input
          type="url"
          style={editorStyles.input}
          value={contact.linkedin_url || ''}
          onChange={(e) => handleChange('linkedin_url', e.target.value)}
          placeholder="https://linkedin.com/in/johndoe"
        />
      </div>

      <div style={editorStyles.row}>
        <div style={editorStyles.fieldGroup}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ ...editorStyles.label, marginBottom: 0 }}>GitHub URL</label>
            <button
              style={editorStyles.visibilityToggle(visibility.github !== false)}
              onClick={() => handleVisibilityChange('github')}
            >
              {visibility.github !== false ? 'Visible' : 'Hidden'}
            </button>
          </div>
          <input
            type="url"
            style={editorStyles.input}
            value={contact.github_url || ''}
            onChange={(e) => handleChange('github_url', e.target.value)}
            placeholder="https://github.com/johndoe"
          />
        </div>

        <div style={editorStyles.fieldGroup}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <label style={{ ...editorStyles.label, marginBottom: 0 }}>Portfolio URL</label>
            <button
              style={editorStyles.visibilityToggle(visibility.portfolio !== false)}
              onClick={() => handleVisibilityChange('portfolio')}
            >
              {visibility.portfolio !== false ? 'Visible' : 'Hidden'}
            </button>
          </div>
          <input
            type="url"
            style={editorStyles.input}
            value={contact.portfolio_url || ''}
            onChange={(e) => handleChange('portfolio_url', e.target.value)}
            placeholder="https://johndoe.com"
          />
        </div>
      </div>
    </div>
  );
};

// Education Editor
const EducationEditor = ({ data, onUpdate }) => {
  const education = data?.education || [];

  const addEducation = () => {
    const newEducation = [...education, {
      school_name: '',
      degree: '',
      major: '',
      minor: '',
      graduation_date: '',
      gpa: '',
      coursework: '',
      honors: ''
    }];
    onUpdate('education', newEducation);
  };

  const updateEducation = (index, field, value) => {
    const newEducation = [...education];
    newEducation[index] = { ...newEducation[index], [field]: value };
    onUpdate('education', newEducation);
  };

  const removeEducation = (index) => {
    const newEducation = education.filter((_, i) => i !== index);
    onUpdate('education', newEducation);
  };

  return (
    <div>
      {education.map((edu, index) => (
        <div key={index} style={editorStyles.card}>
          <div style={editorStyles.cardHeader}>
            <span style={editorStyles.cardTitle}>Education #{index + 1}</span>
            <button style={editorStyles.deleteButton} onClick={() => removeEducation(index)}>
              Delete
            </button>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>School Name</label>
            <input
              type="text"
              style={editorStyles.input}
              value={edu.school_name || ''}
              onChange={(e) => updateEducation(index, 'school_name', e.target.value)}
              placeholder="Stanford University"
            />
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Degree</label>
              <input
                type="text"
                style={editorStyles.input}
                value={edu.degree || ''}
                onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                placeholder="Bachelor of Science"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Major</label>
              <input
                type="text"
                style={editorStyles.input}
                value={edu.major || ''}
                onChange={(e) => updateEducation(index, 'major', e.target.value)}
                placeholder="Computer Science"
              />
            </div>
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Minor (Optional)</label>
              <input
                type="text"
                style={editorStyles.input}
                value={edu.minor || ''}
                onChange={(e) => updateEducation(index, 'minor', e.target.value)}
                placeholder="Mathematics"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Graduation Date</label>
              <input
                type="text"
                style={editorStyles.input}
                value={edu.graduation_date || ''}
                onChange={(e) => updateEducation(index, 'graduation_date', e.target.value)}
                placeholder="May 2024"
              />
            </div>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>GPA (Optional)</label>
            <input
              type="text"
              style={editorStyles.input}
              value={edu.gpa || ''}
              onChange={(e) => updateEducation(index, 'gpa', e.target.value)}
              placeholder="3.8"
            />
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Relevant Coursework (comma-separated)</label>
            <input
              type="text"
              style={editorStyles.input}
              value={edu.coursework || ''}
              onChange={(e) => updateEducation(index, 'coursework', e.target.value)}
              placeholder="Data Structures, Algorithms, Machine Learning"
            />
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Honors (comma-separated)</label>
            <input
              type="text"
              style={editorStyles.input}
              value={edu.honors || ''}
              onChange={(e) => updateEducation(index, 'honors', e.target.value)}
              placeholder="Dean's List, Cum Laude"
            />
          </div>
        </div>
      ))}

      <button style={editorStyles.addButton} onClick={addEducation}>
        + Add Education
      </button>
    </div>
  );
};

// Helper function to parse date strings for sorting
const parseDateForSort = (dateStr) => {
  if (!dateStr) return new Date(0);
  if (dateStr.toLowerCase() === 'present') return new Date();

  // Try to parse common formats like "Jun 2023", "June 2023", "2023"
  const months = {
    'jan': 0, 'january': 0, 'feb': 1, 'february': 1, 'mar': 2, 'march': 2,
    'apr': 3, 'april': 3, 'may': 4, 'jun': 5, 'june': 5, 'jul': 6, 'july': 6,
    'aug': 7, 'august': 7, 'sep': 8, 'september': 8, 'oct': 9, 'october': 9,
    'nov': 10, 'november': 10, 'dec': 11, 'december': 11
  };

  const parts = dateStr.toLowerCase().trim().split(/[\s,]+/);
  let year = new Date().getFullYear();
  let month = 0;

  for (const part of parts) {
    if (months[part] !== undefined) {
      month = months[part];
    } else if (/^\d{4}$/.test(part)) {
      year = parseInt(part);
    }
  }

  return new Date(year, month);
};

// Experience Editor
const ExperienceEditor = ({ data, onUpdate, onSelectBullet, selectedBullet }) => {
  const experience = data?.experience || [];

  const addExperience = () => {
    const newExperience = [...experience, {
      company_name: '',
      job_title: '',
      location: '',
      start_date: '',
      end_date: '',
      bullets: [''],
      technologies: '',
      is_favorite: false
    }];
    onUpdate('experience', newExperience);
  };

  const updateExperience = (index, field, value) => {
    const newExperience = [...experience];
    newExperience[index] = { ...newExperience[index], [field]: value };
    onUpdate('experience', newExperience);
  };

  const removeExperience = (index) => {
    const newExperience = experience.filter((_, i) => i !== index);
    onUpdate('experience', newExperience);
  };

  const moveExperience = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= experience.length) return;

    const newExperience = [...experience];
    const temp = newExperience[index];
    newExperience[index] = newExperience[newIndex];
    newExperience[newIndex] = temp;
    onUpdate('experience', newExperience);
  };

  const sortByDate = () => {
    const sorted = [...experience].sort((a, b) => {
      const dateA = parseDateForSort(a.start_date);
      const dateB = parseDateForSort(b.start_date);
      return dateB - dateA; // Most recent first
    });
    onUpdate('experience', sorted);
  };

  const addBullet = (expIndex) => {
    const newExperience = [...experience];
    newExperience[expIndex].bullets = [...(newExperience[expIndex].bullets || []), ''];
    onUpdate('experience', newExperience);
  };

  const updateBullet = (expIndex, bulletIndex, value) => {
    const newExperience = [...experience];
    newExperience[expIndex].bullets[bulletIndex] = value;
    onUpdate('experience', newExperience);
  };

  const removeBullet = (expIndex, bulletIndex) => {
    const newExperience = [...experience];
    newExperience[expIndex].bullets = newExperience[expIndex].bullets.filter((_, i) => i !== bulletIndex);
    onUpdate('experience', newExperience);
  };

  return (
    <div>
      {experience.length > 1 && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={sortByDate}
            style={{
              padding: '8px 16px',
              background: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: '8px',
              color: 'var(--indigo-400)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            Sort by Date (Recent First)
          </button>
        </div>
      )}
      {experience.map((exp, index) => (
        <div key={index} style={editorStyles.card}>
          <div style={editorStyles.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Reorder buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <button
                  onClick={() => moveExperience(index, -1)}
                  disabled={index === 0}
                  style={{
                    padding: '2px 6px',
                    background: index === 0 ? 'transparent' : 'var(--bg-surface)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '4px',
                    color: index === 0 ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '10px',
                    opacity: index === 0 ? 0.5 : 1
                  }}
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => moveExperience(index, 1)}
                  disabled={index === experience.length - 1}
                  style={{
                    padding: '2px 6px',
                    background: index === experience.length - 1 ? 'transparent' : 'var(--bg-surface)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '4px',
                    color: index === experience.length - 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                    cursor: index === experience.length - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '10px',
                    opacity: index === experience.length - 1 ? 0.5 : 1
                  }}
                  title="Move down"
                >
                  ▼
                </button>
              </div>
              <span style={editorStyles.cardTitle}>Experience #{index + 1}</span>
              <button
                style={{
                  padding: '4px 10px',
                  background: exp.is_favorite ? 'rgba(251, 191, 36, 0.2)' : 'transparent',
                  border: `1px solid ${exp.is_favorite ? 'rgba(251, 191, 36, 0.5)' : 'var(--border-subtle)'}`,
                  borderRadius: '6px',
                  color: exp.is_favorite ? 'var(--amber-400)' : 'var(--text-tertiary)',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                onClick={() => updateExperience(index, 'is_favorite', !exp.is_favorite)}
              >
                {exp.is_favorite ? '★ Favorite' : '☆ Mark Favorite'}
              </button>
            </div>
            <button style={editorStyles.deleteButton} onClick={() => removeExperience(index)}>
              Delete
            </button>
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Company</label>
              <input
                type="text"
                style={editorStyles.input}
                value={exp.company_name || ''}
                onChange={(e) => updateExperience(index, 'company_name', e.target.value)}
                placeholder="Google"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Job Title</label>
              <input
                type="text"
                style={editorStyles.input}
                value={exp.job_title || ''}
                onChange={(e) => updateExperience(index, 'job_title', e.target.value)}
                placeholder="Software Engineer"
              />
            </div>
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Location</label>
              <input
                type="text"
                style={editorStyles.input}
                value={exp.location || ''}
                onChange={(e) => updateExperience(index, 'location', e.target.value)}
                placeholder="Mountain View, CA"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Dates</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  style={{ ...editorStyles.input, flex: 1 }}
                  value={exp.start_date || ''}
                  onChange={(e) => updateExperience(index, 'start_date', e.target.value)}
                  placeholder="Jun 2023"
                />
                <span style={{ color: 'var(--text-tertiary)', alignSelf: 'center' }}>-</span>
                <input
                  type="text"
                  style={{ ...editorStyles.input, flex: 1 }}
                  value={exp.end_date || ''}
                  onChange={(e) => updateExperience(index, 'end_date', e.target.value)}
                  placeholder="Present"
                />
              </div>
            </div>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Technologies (comma-separated)</label>
            <input
              type="text"
              style={editorStyles.input}
              value={exp.technologies || ''}
              onChange={(e) => updateExperience(index, 'technologies', e.target.value)}
              placeholder="React, Python, AWS"
            />
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Bullet Points (click to select for AI help)</label>
            {(exp.bullets || []).map((bullet, bIndex) => {
              const isSelected = selectedBullet?.section === 'experience' &&
                                selectedBullet?.sectionIndex === index &&
                                selectedBullet?.bulletIndex === bIndex;
              return (
                <div
                  key={bIndex}
                  style={editorStyles.bulletItem(isSelected)}
                  onClick={() => onSelectBullet({ section: 'experience', sectionIndex: index, bulletIndex: bIndex })}
                >
                  <textarea
                    style={editorStyles.bulletTextarea}
                    value={bullet || ''}
                    onChange={(e) => updateBullet(index, bIndex, e.target.value)}
                    placeholder="Describe an achievement or responsibility..."
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    style={editorStyles.bulletDeleteBtn}
                    onClick={(e) => { e.stopPropagation(); removeBullet(index, bIndex); }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <button style={editorStyles.addBulletBtn} onClick={() => addBullet(index)}>
              + Add Bullet
            </button>
          </div>
        </div>
      ))}

      <button style={editorStyles.addButton} onClick={addExperience}>
        + Add Experience
      </button>
    </div>
  );
};

// Projects Editor
const ProjectsEditor = ({ data, onUpdate, onSelectBullet, selectedBullet }) => {
  const projects = data?.projects || [];

  const addProject = () => {
    const newProjects = [...projects, {
      project_name: '',
      context: 'Personal',
      date: '',
      bullets: [''],
      technologies: '',
      github_link: '',
      demo_link: '',
      is_favorite: false
    }];
    onUpdate('projects', newProjects);
  };

  const updateProject = (index, field, value) => {
    const newProjects = [...projects];
    newProjects[index] = { ...newProjects[index], [field]: value };
    onUpdate('projects', newProjects);
  };

  const removeProject = (index) => {
    const newProjects = projects.filter((_, i) => i !== index);
    onUpdate('projects', newProjects);
  };

  const moveProject = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= projects.length) return;

    const newProjects = [...projects];
    const temp = newProjects[index];
    newProjects[index] = newProjects[newIndex];
    newProjects[newIndex] = temp;
    onUpdate('projects', newProjects);
  };

  const sortByDate = () => {
    const sorted = [...projects].sort((a, b) => {
      const dateA = parseDateForSort(a.date);
      const dateB = parseDateForSort(b.date);
      return dateB - dateA; // Most recent first
    });
    onUpdate('projects', sorted);
  };

  const addBullet = (projIndex) => {
    const newProjects = [...projects];
    newProjects[projIndex].bullets = [...(newProjects[projIndex].bullets || []), ''];
    onUpdate('projects', newProjects);
  };

  const updateBullet = (projIndex, bulletIndex, value) => {
    const newProjects = [...projects];
    newProjects[projIndex].bullets[bulletIndex] = value;
    onUpdate('projects', newProjects);
  };

  const removeBullet = (projIndex, bulletIndex) => {
    const newProjects = [...projects];
    newProjects[projIndex].bullets = newProjects[projIndex].bullets.filter((_, i) => i !== bulletIndex);
    onUpdate('projects', newProjects);
  };

  const contextOptions = ['Personal', 'Hackathon', 'University', 'Open Source', 'Work'];

  return (
    <div>
      {projects.length > 1 && (
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={sortByDate}
            style={{
              padding: '8px 16px',
              background: 'rgba(99, 102, 241, 0.1)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              borderRadius: '8px',
              color: 'var(--indigo-400)',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '500'
            }}
          >
            Sort by Date (Recent First)
          </button>
        </div>
      )}
      {projects.map((proj, index) => (
        <div key={index} style={editorStyles.card}>
          <div style={editorStyles.cardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* Reorder buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <button
                  onClick={() => moveProject(index, -1)}
                  disabled={index === 0}
                  style={{
                    padding: '2px 6px',
                    background: index === 0 ? 'transparent' : 'var(--bg-surface)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '4px',
                    color: index === 0 ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                    cursor: index === 0 ? 'not-allowed' : 'pointer',
                    fontSize: '10px',
                    opacity: index === 0 ? 0.5 : 1
                  }}
                  title="Move up"
                >
                  ▲
                </button>
                <button
                  onClick={() => moveProject(index, 1)}
                  disabled={index === projects.length - 1}
                  style={{
                    padding: '2px 6px',
                    background: index === projects.length - 1 ? 'transparent' : 'var(--bg-surface)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: '4px',
                    color: index === projects.length - 1 ? 'var(--text-tertiary)' : 'var(--text-secondary)',
                    cursor: index === projects.length - 1 ? 'not-allowed' : 'pointer',
                    fontSize: '10px',
                    opacity: index === projects.length - 1 ? 0.5 : 1
                  }}
                  title="Move down"
                >
                  ▼
                </button>
              </div>
              <span style={editorStyles.cardTitle}>Project #{index + 1}</span>
              <button
                style={{
                  padding: '4px 10px',
                  background: proj.is_favorite ? 'rgba(251, 191, 36, 0.2)' : 'transparent',
                  border: `1px solid ${proj.is_favorite ? 'rgba(251, 191, 36, 0.5)' : 'var(--border-subtle)'}`,
                  borderRadius: '6px',
                  color: proj.is_favorite ? 'var(--amber-400)' : 'var(--text-tertiary)',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                onClick={() => updateProject(index, 'is_favorite', !proj.is_favorite)}
              >
                {proj.is_favorite ? '★ Favorite' : '☆ Mark Favorite'}
              </button>
            </div>
            <button style={editorStyles.deleteButton} onClick={() => removeProject(index)}>
              Delete
            </button>
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Project Name</label>
              <input
                type="text"
                style={editorStyles.input}
                value={proj.project_name || ''}
                onChange={(e) => updateProject(index, 'project_name', e.target.value)}
                placeholder="AI Resume Builder"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Context</label>
              <select
                style={editorStyles.input}
                value={proj.context || 'Personal'}
                onChange={(e) => updateProject(index, 'context', e.target.value)}
              >
                {contextOptions.map(opt => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Date</label>
              <input
                type="text"
                style={editorStyles.input}
                value={proj.date || ''}
                onChange={(e) => updateProject(index, 'date', e.target.value)}
                placeholder="Jan 2024"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Technologies</label>
              <input
                type="text"
                style={editorStyles.input}
                value={proj.technologies || ''}
                onChange={(e) => updateProject(index, 'technologies', e.target.value)}
                placeholder="React, Node.js, MongoDB"
              />
            </div>
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>GitHub Link (Optional)</label>
              <input
                type="url"
                style={editorStyles.input}
                value={proj.github_link || ''}
                onChange={(e) => updateProject(index, 'github_link', e.target.value)}
                placeholder="https://github.com/..."
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Demo Link (Optional)</label>
              <input
                type="url"
                style={editorStyles.input}
                value={proj.demo_link || ''}
                onChange={(e) => updateProject(index, 'demo_link', e.target.value)}
                placeholder="https://myproject.com"
              />
            </div>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Bullet Points (click to select for AI help)</label>
            {(proj.bullets || []).map((bullet, bIndex) => {
              const isSelected = selectedBullet?.section === 'project' &&
                                selectedBullet?.sectionIndex === index &&
                                selectedBullet?.bulletIndex === bIndex;
              return (
                <div
                  key={bIndex}
                  style={editorStyles.bulletItem(isSelected)}
                  onClick={() => onSelectBullet({ section: 'project', sectionIndex: index, bulletIndex: bIndex })}
                >
                  <textarea
                    style={editorStyles.bulletTextarea}
                    value={bullet || ''}
                    onChange={(e) => updateBullet(index, bIndex, e.target.value)}
                    placeholder="Describe what you built or achieved..."
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    style={editorStyles.bulletDeleteBtn}
                    onClick={(e) => { e.stopPropagation(); removeBullet(index, bIndex); }}
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <button style={editorStyles.addBulletBtn} onClick={() => addBullet(index)}>
              + Add Bullet
            </button>
          </div>
        </div>
      ))}

      <button style={editorStyles.addButton} onClick={addProject}>
        + Add Project
      </button>
    </div>
  );
};

// Skills Editor
const SkillsEditor = ({ data, onUpdate }) => {
  const skills = data?.skills || {};

  const handleChange = (field, value) => {
    onUpdate(`skills.${field}`, value);
  };

  return (
    <div>
      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Programming Languages (comma-separated)</label>
        <input
          type="text"
          style={editorStyles.input}
          value={skills.languages || ''}
          onChange={(e) => handleChange('languages', e.target.value)}
          placeholder="Python, JavaScript, Java, C++"
        />
      </div>

      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Frontend (comma-separated)</label>
        <input
          type="text"
          style={editorStyles.input}
          value={skills.frontend || ''}
          onChange={(e) => handleChange('frontend', e.target.value)}
          placeholder="React, Vue.js, TypeScript, HTML/CSS"
        />
      </div>

      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Backend (comma-separated)</label>
        <input
          type="text"
          style={editorStyles.input}
          value={skills.backend || ''}
          onChange={(e) => handleChange('backend', e.target.value)}
          placeholder="Node.js, Django, FastAPI, Spring Boot"
        />
      </div>

      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Databases (comma-separated)</label>
        <input
          type="text"
          style={editorStyles.input}
          value={skills.databases || ''}
          onChange={(e) => handleChange('databases', e.target.value)}
          placeholder="PostgreSQL, MongoDB, Redis, MySQL"
        />
      </div>

      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Cloud & DevOps (comma-separated)</label>
        <input
          type="text"
          style={editorStyles.input}
          value={skills.cloud_devops || ''}
          onChange={(e) => handleChange('cloud_devops', e.target.value)}
          placeholder="AWS, Docker, Kubernetes, CI/CD"
        />
      </div>

      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Tools (comma-separated)</label>
        <input
          type="text"
          style={editorStyles.input}
          value={skills.tools || ''}
          onChange={(e) => handleChange('tools', e.target.value)}
          placeholder="Git, Jira, Figma, VS Code"
        />
      </div>
    </div>
  );
};

// Certifications Editor
const CertificationsEditor = ({ data, onUpdate }) => {
  const certifications = data?.certifications || [];

  const addCertification = () => {
    const newCerts = [...certifications, {
      cert_name: '',
      issuing_org: '',
      date_obtained: '',
      expiration_date: ''
    }];
    onUpdate('certifications', newCerts);
  };

  const updateCertification = (index, field, value) => {
    const newCerts = [...certifications];
    newCerts[index] = { ...newCerts[index], [field]: value };
    onUpdate('certifications', newCerts);
  };

  const removeCertification = (index) => {
    const newCerts = certifications.filter((_, i) => i !== index);
    onUpdate('certifications', newCerts);
  };

  return (
    <div>
      {certifications.map((cert, index) => (
        <div key={index} style={editorStyles.card}>
          <div style={editorStyles.cardHeader}>
            <span style={editorStyles.cardTitle}>Certification #{index + 1}</span>
            <button style={editorStyles.deleteButton} onClick={() => removeCertification(index)}>
              Delete
            </button>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Certification Name</label>
            <input
              type="text"
              style={editorStyles.input}
              value={cert.cert_name || ''}
              onChange={(e) => updateCertification(index, 'cert_name', e.target.value)}
              placeholder="AWS Solutions Architect"
            />
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Issuing Organization</label>
              <input
                type="text"
                style={editorStyles.input}
                value={cert.issuing_org || ''}
                onChange={(e) => updateCertification(index, 'issuing_org', e.target.value)}
                placeholder="Amazon Web Services"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Date Obtained</label>
              <input
                type="text"
                style={editorStyles.input}
                value={cert.date_obtained || ''}
                onChange={(e) => updateCertification(index, 'date_obtained', e.target.value)}
                placeholder="Jan 2024"
              />
            </div>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Expiration Date (Optional)</label>
            <input
              type="text"
              style={editorStyles.input}
              value={cert.expiration_date || ''}
              onChange={(e) => updateCertification(index, 'expiration_date', e.target.value)}
              placeholder="Jan 2027"
            />
          </div>
        </div>
      ))}

      <button style={editorStyles.addButton} onClick={addCertification}>
        + Add Certification
      </button>
    </div>
  );
};

// Honors & Awards Editor
const HonorsEditor = ({ data, onUpdate }) => {
  const honors = data?.honors || [];

  const addHonor = () => {
    const newHonors = [...honors, {
      award_name: '',
      issuing_org: '',
      date: '',
      description: ''
    }];
    onUpdate('honors', newHonors);
  };

  const updateHonor = (index, field, value) => {
    const newHonors = [...honors];
    newHonors[index] = { ...newHonors[index], [field]: value };
    onUpdate('honors', newHonors);
  };

  const removeHonor = (index) => {
    const newHonors = honors.filter((_, i) => i !== index);
    onUpdate('honors', newHonors);
  };

  return (
    <div>
      {honors.map((honor, index) => (
        <div key={index} style={editorStyles.card}>
          <div style={editorStyles.cardHeader}>
            <span style={editorStyles.cardTitle}>Award #{index + 1}</span>
            <button style={editorStyles.deleteButton} onClick={() => removeHonor(index)}>
              Delete
            </button>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Award Name</label>
            <input
              type="text"
              style={editorStyles.input}
              value={honor.award_name || ''}
              onChange={(e) => updateHonor(index, 'award_name', e.target.value)}
              placeholder="Dean's List"
            />
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Issuing Organization</label>
              <input
                type="text"
                style={editorStyles.input}
                value={honor.issuing_org || ''}
                onChange={(e) => updateHonor(index, 'issuing_org', e.target.value)}
                placeholder="Stanford University"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Date</label>
              <input
                type="text"
                style={editorStyles.input}
                value={honor.date || ''}
                onChange={(e) => updateHonor(index, 'date', e.target.value)}
                placeholder="Spring 2024"
              />
            </div>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Description (Optional)</label>
            <textarea
              style={editorStyles.textarea}
              value={honor.description || ''}
              onChange={(e) => updateHonor(index, 'description', e.target.value)}
              placeholder="Brief description of the award..."
            />
          </div>
        </div>
      ))}

      <button style={editorStyles.addButton} onClick={addHonor}>
        + Add Honor/Award
      </button>
    </div>
  );
};

// Leadership Editor
const LeadershipEditor = ({ data, onUpdate }) => {
  const leadership = data?.leadership || [];

  const addLeadership = () => {
    const newLeadership = [...leadership, {
      organization: '',
      role: '',
      dates: '',
      achievements: ''
    }];
    onUpdate('leadership', newLeadership);
  };

  const updateLeadership = (index, field, value) => {
    const newLeadership = [...leadership];
    newLeadership[index] = { ...newLeadership[index], [field]: value };
    onUpdate('leadership', newLeadership);
  };

  const removeLeadership = (index) => {
    const newLeadership = leadership.filter((_, i) => i !== index);
    onUpdate('leadership', newLeadership);
  };

  return (
    <div>
      {leadership.map((lead, index) => (
        <div key={index} style={editorStyles.card}>
          <div style={editorStyles.cardHeader}>
            <span style={editorStyles.cardTitle}>Activity #{index + 1}</span>
            <button style={editorStyles.deleteButton} onClick={() => removeLeadership(index)}>
              Delete
            </button>
          </div>

          <div style={editorStyles.row}>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Organization</label>
              <input
                type="text"
                style={editorStyles.input}
                value={lead.organization || ''}
                onChange={(e) => updateLeadership(index, 'organization', e.target.value)}
                placeholder="Computer Science Club"
              />
            </div>
            <div style={editorStyles.fieldGroup}>
              <label style={editorStyles.label}>Role</label>
              <input
                type="text"
                style={editorStyles.input}
                value={lead.role || ''}
                onChange={(e) => updateLeadership(index, 'role', e.target.value)}
                placeholder="President"
              />
            </div>
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Dates</label>
            <input
              type="text"
              style={editorStyles.input}
              value={lead.dates || ''}
              onChange={(e) => updateLeadership(index, 'dates', e.target.value)}
              placeholder="Sep 2022 - Present"
            />
          </div>

          <div style={editorStyles.fieldGroup}>
            <label style={editorStyles.label}>Achievements (comma-separated)</label>
            <textarea
              style={editorStyles.textarea}
              value={lead.achievements || ''}
              onChange={(e) => updateLeadership(index, 'achievements', e.target.value)}
              placeholder="Organized 10+ workshops, Grew membership by 50%"
            />
          </div>
        </div>
      ))}

      <button style={editorStyles.addButton} onClick={addLeadership}>
        + Add Leadership/Activity
      </button>
    </div>
  );
};

// Constants for Job Preferences (matching onboarding)
const CAREER_STATUSES = [
  { id: 'student', label: 'Student' },
  { id: 'recent_grad', label: 'Recent Graduate' },
  { id: 'employed', label: 'Currently Employed' },
  { id: 'between_roles', label: 'Between Roles' },
];

const TARGET_ROLES = [
  'Backend Developer',
  'Frontend Developer',
  'Full Stack Developer',
  'DevOps Engineer',
  'Data Scientist',
  'Machine Learning Engineer',
  'iOS Developer',
  'Android Developer',
  'Software Engineer',
  'Site Reliability Engineer',
  'Cloud Engineer',
  'Security Engineer',
  'QA Engineer',
  'Product Manager',
  'Engineering Manager',
];

const LOCATIONS = [
  'Remote',
  'San Francisco, CA',
  'New York, NY',
  'Seattle, WA',
  'Austin, TX',
  'Los Angeles, CA',
  'Boston, MA',
  'Denver, CO',
  'Chicago, IL',
  'Atlanta, GA',
  'Portland, OR',
  'San Diego, CA',
];

const REMOTE_PREFERENCES = [
  { id: 'remote_only', label: 'Remote Only' },
  { id: 'hybrid', label: 'Hybrid' },
  { id: 'on_site', label: 'On-site' },
  { id: 'flexible', label: 'Flexible' },
];

// Job Preferences Editor Component
const JobPreferencesEditor = ({ data, onSave, saving }) => {
  const [localData, setLocalData] = useState({
    career_status: data?.career_status || '',
    target_roles: data?.target_roles || [],
    preferred_locations: data?.preferred_locations || [],
    remote_preference: data?.remote_preference || '',
    years_experience: data?.years_experience ?? ''
  });
  const [roleSearch, setRoleSearch] = useState('');
  const [locationSearch, setLocationSearch] = useState('');

  const toggleRole = (role) => {
    setLocalData(prev => ({
      ...prev,
      target_roles: prev.target_roles.includes(role)
        ? prev.target_roles.filter(r => r !== role)
        : [...prev.target_roles, role]
    }));
  };

  const toggleLocation = (location) => {
    setLocalData(prev => ({
      ...prev,
      preferred_locations: prev.preferred_locations.includes(location)
        ? prev.preferred_locations.filter(l => l !== location)
        : [...prev.preferred_locations, location]
    }));
  };

  const filteredRoles = TARGET_ROLES.filter(role =>
    role.toLowerCase().includes(roleSearch.toLowerCase())
  );

  const filteredLocations = LOCATIONS.filter(loc =>
    loc.toLowerCase().includes(locationSearch.toLowerCase())
  );

  return (
    <div>
      {/* Career Status */}
      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Career Status</label>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {CAREER_STATUSES.map(status => (
            <button
              key={status.id}
              onClick={() => setLocalData(prev => ({ ...prev, career_status: status.id }))}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: localData.career_status === status.id
                  ? '2px solid var(--indigo-500)'
                  : '1px solid var(--border-subtle)',
                background: localData.career_status === status.id
                  ? 'rgba(99, 102, 241, 0.15)'
                  : 'var(--bg-surface)',
                color: localData.career_status === status.id
                  ? 'var(--indigo-400)'
                  : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              {status.label}
            </button>
          ))}
        </div>
      </div>

      {/* Target Roles */}
      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Target Roles</label>
        <input
          type="text"
          style={{ ...editorStyles.input, marginBottom: '12px' }}
          placeholder="Search roles..."
          value={roleSearch}
          onChange={(e) => setRoleSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', maxHeight: '150px', overflowY: 'auto' }}>
          {filteredRoles.map(role => (
            <button
              key={role}
              onClick={() => toggleRole(role)}
              style={{
                padding: '8px 14px',
                borderRadius: '20px',
                border: localData.target_roles.includes(role)
                  ? '1px solid var(--indigo-500)'
                  : '1px solid var(--border-subtle)',
                background: localData.target_roles.includes(role)
                  ? 'rgba(99, 102, 241, 0.15)'
                  : 'var(--bg-surface)',
                color: localData.target_roles.includes(role)
                  ? 'var(--indigo-400)'
                  : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s'
              }}
            >
              {localData.target_roles.includes(role) ? '✓ ' : ''}{role}
            </button>
          ))}
        </div>
      </div>

      {/* Preferred Locations */}
      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Preferred Locations</label>
        <input
          type="text"
          style={{ ...editorStyles.input, marginBottom: '12px' }}
          placeholder="Search locations..."
          value={locationSearch}
          onChange={(e) => setLocationSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', maxHeight: '150px', overflowY: 'auto' }}>
          {filteredLocations.map(location => (
            <button
              key={location}
              onClick={() => toggleLocation(location)}
              style={{
                padding: '8px 14px',
                borderRadius: '20px',
                border: localData.preferred_locations.includes(location)
                  ? '1px solid var(--emerald-500)'
                  : '1px solid var(--border-subtle)',
                background: localData.preferred_locations.includes(location)
                  ? 'rgba(16, 185, 129, 0.15)'
                  : 'var(--bg-surface)',
                color: localData.preferred_locations.includes(location)
                  ? 'var(--emerald-400)'
                  : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '13px',
                transition: 'all 0.2s'
              }}
            >
              {localData.preferred_locations.includes(location) ? '✓ ' : ''}{location}
            </button>
          ))}
        </div>
      </div>

      {/* Remote Preference */}
      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Remote Preference</label>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {REMOTE_PREFERENCES.map(pref => (
            <button
              key={pref.id}
              onClick={() => setLocalData(prev => ({ ...prev, remote_preference: pref.id }))}
              style={{
                padding: '10px 20px',
                borderRadius: '10px',
                border: localData.remote_preference === pref.id
                  ? '2px solid var(--purple-500)'
                  : '1px solid var(--border-subtle)',
                background: localData.remote_preference === pref.id
                  ? 'rgba(168, 85, 247, 0.15)'
                  : 'var(--bg-surface)',
                color: localData.remote_preference === pref.id
                  ? 'var(--purple-400)'
                  : 'var(--text-secondary)',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'all 0.2s'
              }}
            >
              {pref.label}
            </button>
          ))}
        </div>
      </div>

      {/* Years of Experience */}
      <div style={editorStyles.fieldGroup}>
        <label style={editorStyles.label}>Years of Experience</label>
        <input
          type="number"
          min="0"
          max="50"
          style={{ ...editorStyles.input, maxWidth: '150px' }}
          value={localData.years_experience}
          onChange={(e) => setLocalData(prev => ({ ...prev, years_experience: e.target.value }))}
          placeholder="0"
        />
      </div>

      {/* Save Button */}
      <button
        onClick={() => onSave(localData)}
        disabled={saving}
        style={{
          width: '100%',
          padding: '14px',
          background: 'var(--gradient-primary)',
          border: 'none',
          borderRadius: '12px',
          color: 'white',
          fontSize: '16px',
          fontWeight: '600',
          cursor: saving ? 'wait' : 'pointer',
          opacity: saving ? 0.7 : 1,
          marginTop: '24px',
          transition: 'all 0.2s'
        }}
      >
        {saving ? 'Saving...' : 'Save Job Preferences'}
      </button>
    </div>
  );
};

export default Profile;
